import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Car, Clock, Search, Users, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api-client';
import { useActiveClient } from '@/contexts/ActiveClientContext';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermission } from '@/hooks/usePermission';
import type { VeiculoListResponse, Veiculo } from '@/services/veiculo';
import type { MotoristaListResponse, Motorista } from '@/services/motorista';

// GlobalSearch - autocomplete unificado de veiculos e motoristas no header.
// Equivale ao GlobalSearch do eFleet (origem do pattern), adaptado ao Frotas:
//   - Endpoints: /v1/veiculos?busca= e /v1/motoristas?busca= (ja suportam search)
//   - Resposta: { data, meta } (Frotas usa "data", eFleet usa "items")
//   - IDs publicos sao UUID v7 (string), navegacao usa /veiculos/:uuid
//   - Permissao no formato 'modulo:acao' (Frotas) em vez de can(mod, acao) (eFleet)
//   - Cores e tokens do design system roxo da JUST
//
// Comportamento:
//   - Debounce 250ms, fetch so apartir de 2 chars
//   - 6 resultados por entidade (12 maximo)
//   - Histórico das ultimas 6 selecoes em localStorage
//   - Atalho Cmd+K / Ctrl+K pra focar e abrir
//   - Setas pra navegar, Enter pra selecionar, Esc pra fechar
//   - Click fora fecha o dropdown
//   - Esconde resultados de modulo sem permissao (skip do fetch)

const HISTORY_KEY_PREFIX = 'frotas:search:history';
const HISTORY_MAX = 6;
const SEARCH_PAGE_SIZE = 6;

interface HistoryEntry {
  type: 'veiculo' | 'motorista';
  uuid: string;
  label: string;
  sub: string;
}

// Fix 2026-07-13: historico era uma chave global unica no localStorage,
// entao trocar de cliente ativo continuava mostrando veiculos/motoristas do
// cliente ANTERIOR - clicar neles dava 404 (o tenant scope do backend
// bloqueia corretamente, mas o item nem deveria aparecer). Chave agora e
// namespaced por activeClientUuid, igual o resto do app trata troca de
// cliente (ver queryClient.clear() em ActiveClientContext.switchClient).
function historyKey(activeClientUuid: string | null): string {
  return `${HISTORY_KEY_PREFIX}:${activeClientUuid ?? 'sem-cliente'}`;
}

function loadHistory(activeClientUuid: string | null): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(historyKey(activeClientUuid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToHistory(entry: HistoryEntry, activeClientUuid: string | null) {
  try {
    const current = loadHistory(activeClientUuid).filter(
      (h) => !(h.type === entry.type && h.uuid === entry.uuid),
    );
    const next = [entry, ...current].slice(0, HISTORY_MAX);
    localStorage.setItem(historyKey(activeClientUuid), JSON.stringify(next));
  } catch {
    /* noop: localStorage indisponivel (modo privado, quota) */
  }
}

// Formatadores ────────────────────────────────────────────────

function fmtPlaca(placa: string | null | undefined): string {
  if (!placa) return '-';
  return placa.toUpperCase();
}

function fmtCpf(cpf: string | null | undefined): string {
  if (!cpf) return '';
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

// Subcomponentes ──────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pb-1 pt-2.5 text-[9.5px] font-extrabold uppercase tracking-widest text-text-subtle">
      {children}
    </div>
  );
}

interface ResultItemProps {
  icon: React.ReactNode;
  label: string;
  sub: string;
  active: boolean;
  onClick: () => void;
}

function ResultItem({ icon, label, sub, active, onClick }: ResultItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors',
        active
          ? 'bg-primary-soft text-text-strong'
          : 'hover:bg-surface-soft text-text-strong',
      )}
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-mute text-text-muted">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold">{label}</div>
        {sub && <div className="truncate text-[11px] text-text-subtle">{sub}</div>}
      </div>
      <ArrowUpRight size={13} className="shrink-0 text-text-subtle" />
    </button>
  );
}

function HistoryItem({
  entry,
  active,
  onClick,
  onRemove,
}: {
  entry: HistoryEntry;
  active: boolean;
  onClick: () => void;
  onRemove: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors cursor-pointer',
        active ? 'bg-primary-soft' : 'hover:bg-surface-soft',
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Clock size={13} className="shrink-0 text-text-subtle" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold text-text-strong">
          {entry.label}
        </div>
        {entry.sub && (
          <div className="truncate text-[11px] text-text-subtle">{entry.sub}</div>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remover do histórico"
        className="hidden shrink-0 rounded p-0.5 text-text-subtle transition-colors hover:bg-surface-mute hover:text-text-muted group-hover:flex"
      >
        <X size={11} />
      </button>
    </div>
  );
}

// Componente principal ────────────────────────────────────────

interface SearchResults {
  veiculos: Veiculo[];
  motoristas: Motorista[];
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { activeClientUuid } = useActiveClient();

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeIdx, setActiveIdx] = React.useState(-1);
  const [history, setHistory] = React.useState<HistoryEntry[]>(() =>
    loadHistory(activeClientUuid),
  );
  const [isMac, setIsMac] = React.useState(false);

  // Recarrega o historico quando o cliente ativo muda (troca de cliente ou
  // resolucao inicial null -> uuid real) - historico e escopado por cliente.
  React.useEffect(() => {
    setHistory(loadHistory(activeClientUuid));
  }, [activeClientUuid]);

  React.useEffect(() => {
    setIsMac(/Mac|iPhone|iPad/.test(navigator.platform));
  }, []);

  const debouncedQuery = useDebounce(query, 250);

  const canSeeVeiculos = usePermission('veiculos.visualizar');
  const canSeeMotoristas = usePermission('motoristas.visualizar');

  // Fetch ────────────────────────────────────────────────────

  const { data: results, isFetching } = useQuery<SearchResults>({
    queryKey: ['global-search', debouncedQuery, canSeeVeiculos, canSeeMotoristas],
    queryFn: async () => {
      const q = debouncedQuery.trim();
      const [veiculosRes, motoristasRes] = await Promise.all([
        canSeeVeiculos
          ? apiClient
              .request<VeiculoListResponse>(
                `/veiculos${apiClient.buildQueryString({ busca: q, page_size: SEARCH_PAGE_SIZE })}`,
              )
              .then((r) => r.data)
          : Promise.resolve([] as Veiculo[]),
        canSeeMotoristas
          ? apiClient
              .request<MotoristaListResponse>(
                `/motoristas${apiClient.buildQueryString({ busca: q, page_size: SEARCH_PAGE_SIZE })}`,
              )
              .then((r) => r.data)
          : Promise.resolve([] as Motorista[]),
      ]);
      return { veiculos: veiculosRes, motoristas: motoristasRes };
    },
    enabled: debouncedQuery.trim().length >= 2,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  // Flatten de itens navegaveis pra setas/Enter ──────────────

  type NavItem =
    | { kind: 'veiculo'; item: Veiculo }
    | { kind: 'motorista'; item: Motorista }
    | { kind: 'history'; entry: HistoryEntry };

  const navItems: NavItem[] = React.useMemo(() => {
    const q = debouncedQuery.trim();
    if (!q || q.length < 2) {
      return history.map((entry) => ({ kind: 'history' as const, entry }));
    }
    return [
      ...(results?.veiculos ?? []).map((item) => ({ kind: 'veiculo' as const, item })),
      ...(results?.motoristas ?? []).map((item) => ({ kind: 'motorista' as const, item })),
    ];
  }, [debouncedQuery, results, history]);

  // Navegacao ────────────────────────────────────────────────

  function navigateTo(entry: HistoryEntry) {
    saveToHistory(entry, activeClientUuid);
    setHistory(loadHistory(activeClientUuid));
    setOpen(false);
    setQuery('');
    setActiveIdx(-1);
    navigate(entry.type === 'veiculo' ? `/veiculos/${entry.uuid}` : `/motoristas/${entry.uuid}`);
  }

  function selectVeiculo(v: Veiculo) {
    navigateTo({
      type: 'veiculo',
      uuid: v.id,
      label: `${fmtPlaca(v.placa)}${v.patrimonio ? ` · ${v.patrimonio}` : ''}`,
      sub: v.descricao ?? '',
    });
  }

  function selectMotorista(m: Motorista) {
    const matriculaStr = m.matricula ?? '';
    navigateTo({
      type: 'motorista',
      uuid: m.id,
      label: m.nome,
      sub: [m.cpf ? fmtCpf(m.cpf) : '', matriculaStr].filter(Boolean).join(' · '),
    });
  }

  function selectNavItem(idx: number) {
    const ni = navItems[idx];
    if (!ni) return;
    if (ni.kind === 'veiculo') selectVeiculo(ni.item);
    else if (ni.kind === 'motorista') selectMotorista(ni.item);
    else navigateTo(ni.entry);
  }

  function removeHistory(entry: HistoryEntry, e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const next = loadHistory(activeClientUuid).filter(
        (h) => !(h.type === entry.type && h.uuid === entry.uuid),
      );
      localStorage.setItem(historyKey(activeClientUuid), JSON.stringify(next));
      setHistory(next);
    } catch {
      /* noop */
    }
  }

  // Keyboard ─────────────────────────────────────────────────

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, navItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      selectNavItem(activeIdx);
    }
  }

  // Atalho Cmd+K / Ctrl+K ────────────────────────────────────

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Fechar ao clicar fora ────────────────────────────────────

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reset do indice ativo ao mudar resultados/historico
  React.useEffect(() => {
    setActiveIdx(-1);
  }, [navItems]);

  // Helpers de render ────────────────────────────────────────

  const showHistory = open && debouncedQuery.trim().length < 2 && history.length > 0;
  const showResults = open && debouncedQuery.trim().length >= 2;
  const hasResults =
    (results?.veiculos?.length ?? 0) + (results?.motoristas?.length ?? 0) > 0;

  // Indices relativos a cada secao pra highlight do activeIdx
  const veiculosCount = results?.veiculos?.length ?? 0;
  const getHistoryIdx = (i: number) => i;
  const getVeiculoIdx = (i: number) => i;
  const getMotoristaIdx = (i: number) => veiculosCount + i;

  return (
    <div ref={containerRef} className="relative w-[360px]">
      {/* Input */}
      <Search
        size={16}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle z-10"
      />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Buscar veículos e motoristas..."
        className={cn(
          'h-10 w-full rounded-lg border border-border bg-surface-soft',
          'pl-9 pr-12 text-sm text-text-strong',
          'placeholder:text-text-subtle',
          'transition-all hover:border-border-strong',
          'focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary focus:bg-surface',
        )}
      />
      <kbd className="absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border-strong/50 bg-surface px-1.5 py-0.5 text-[10px] font-mono font-semibold text-text-subtle">
        {isMac ? '⌘' : 'Ctrl'}K
      </kbd>

      {/* Dropdown */}
      {(showHistory || showResults) && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-dropdown overflow-hidden rounded-2xl border border-border bg-surface shadow-pop"
        >
          {/* Histórico ─────────────────────────────────── */}
          {showHistory && (
            <>
              <SectionTitle>Histórico</SectionTitle>
              <div className="p-1.5 pt-0">
                {history.map((entry, i) => (
                  <HistoryItem
                    key={`${entry.type}-${entry.uuid}`}
                    entry={entry}
                    active={activeIdx === getHistoryIdx(i)}
                    onClick={() => navigateTo(entry)}
                    onRemove={(e) => removeHistory(entry, e)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Resultados ─────────────────────────────────── */}
          {showResults && (
            <>
              {isFetching && !hasResults && (
                <div className="px-4 py-6 text-center text-[12px] text-text-subtle">
                  Buscando...
                </div>
              )}

              {!isFetching && !hasResults && (
                <div className="px-4 py-6 text-center text-[12px] text-text-subtle">
                  Nenhum resultado para <strong>{debouncedQuery}</strong>
                </div>
              )}

              {(results?.veiculos?.length ?? 0) > 0 && (
                <>
                  <SectionTitle>Veículos</SectionTitle>
                  <div className="p-1.5 pt-0">
                    {results!.veiculos.map((v, i) => (
                      <ResultItem
                        key={v.id}
                        icon={<Car size={13} />}
                        label={`${fmtPlaca(v.placa)}${v.patrimonio ? ` · ${v.patrimonio}` : ''}`}
                        sub={v.descricao ?? ''}
                        active={activeIdx === getVeiculoIdx(i)}
                        onClick={() => selectVeiculo(v)}
                      />
                    ))}
                  </div>
                </>
              )}

              {(results?.motoristas?.length ?? 0) > 0 && (
                <>
                  <SectionTitle>Motoristas</SectionTitle>
                  <div className="p-1.5 pt-0">
                    {results!.motoristas.map((m, i) => (
                      <ResultItem
                        key={m.id}
                        icon={<Users size={13} />}
                        label={m.nome}
                        sub={[
                          m.cpf ? fmtCpf(m.cpf) : '',
                          m.matricula ?? '',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        active={activeIdx === getMotoristaIdx(i)}
                        onClick={() => selectMotorista(m)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Footer com dicas de teclado */}
          <div className="flex items-center gap-4 border-t border-border px-4 py-2">
            <span className="text-[10px] text-text-subtle">
              <kbd className="rounded border border-border bg-surface-soft px-1 py-px text-[9px] font-bold">
                ↑↓
              </kbd>{' '}
              navegar
            </span>
            <span className="text-[10px] text-text-subtle">
              <kbd className="rounded border border-border bg-surface-soft px-1 py-px text-[9px] font-bold">
                ↵
              </kbd>{' '}
              selecionar
            </span>
            <span className="text-[10px] text-text-subtle">
              <kbd className="rounded border border-border bg-surface-soft px-1 py-px text-[9px] font-bold">
                Esc
              </kbd>{' '}
              fechar
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
